import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useState } from "react";

interface MergedStartDayData {
  email: string;
  userLastModifiedDate: string;
  startDate: string;
  lastImplementationDate: string;
  implementationCount: number;
}

type Order = "asc" | "desc";
type OrderBy =
  | "email"
  | "userLastModifiedDate"
  | "startDate"
  | "lastImplementationDate";

interface UserProgressDashBoardProps {
  mergedStartDayData: MergedStartDayData[];
}

export const UserProgressDashBoard: React.FC<UserProgressDashBoardProps> = ({
  mergedStartDayData,
}) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<OrderBy>("startDate");

  const formatAnswerDate = (dateString: string) => {
    if (!dateString) return "";
    // -postと-preを削除
    return dateString.replace(/-post|-pre/g, "");
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // 月は0始まりなので+1
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}${month}${day}`;
  };

  const downLoadCSV = () => {
    // sortedDataをCSVとしてダウンロード
    // CSVヘッダー
    const csvHeader =
      "メールアドレス,パスワード変更日,開始日,最終実施日,実施回数\n";

    // CSVデータ
    const csvData = sortedData
      .map((row) => {
        const email = row.email;
        const userLastModifiedDate = formatDate(row.userLastModifiedDate);
        const startDate = formatAnswerDate(row.startDate);
        const lastImplementationDate = formatAnswerDate(
          row.lastImplementationDate
        );
        const implementationCount = row.implementationCount;

        return `${email},${userLastModifiedDate},${startDate},${lastImplementationDate},${implementationCount}`;
      })
      .join("\n");

    // CSV文字列を作成
    const csvContent = csvHeader + csvData;

    // BOMを追加してExcelで文字化けを防ぐ
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    // ダウンロード用のリンクを作成
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    // ファイル名を現在の日時で生成
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/:/g, "-");
    link.setAttribute("download", `参加者データ_${timestamp}.csv`);

    // ダウンロードを実行
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // URLオブジェクトを解放
    URL.revokeObjectURL(url);
  };

  const sortData = (data: MergedStartDayData[]) => {
    return data.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // 日付の場合は特別処理
      if (orderBy === "startDate") {
        aValue = formatAnswerDate(a.startDate);
        bValue = formatAnswerDate(b.startDate);
      }

      if (order === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = sortData([...mergedStartDayData]);

  return (
    <>
      <Box sx={{ mt: 1, p: 1 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          参加者実施状況
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "email"}
                    direction={orderBy === "email" ? order : "asc"}
                    onClick={() => handleRequestSort("email")}
                  >
                    メールアドレス
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "userLastModifiedDate"}
                    direction={
                      orderBy === "userLastModifiedDate" ? order : "asc"
                    }
                    onClick={() => handleRequestSort("userLastModifiedDate")}
                  >
                    パスワード変更日
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "startDate"}
                    direction={orderBy === "startDate" ? order : "asc"}
                    onClick={() => handleRequestSort("startDate")}
                  >
                    開始日
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "lastImplementationDate"}
                    direction={
                      orderBy === "lastImplementationDate" ? order : "asc"
                    }
                    onClick={() => handleRequestSort("lastImplementationDate")}
                  >
                    最終実施日
                  </TableSortLabel>
                </TableCell>
                <TableCell>実施回数</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{formatDate(row.userLastModifiedDate)}</TableCell>
                  <TableCell>{formatAnswerDate(row.startDate)}</TableCell>
                  <TableCell>
                    {formatAnswerDate(row.lastImplementationDate)}
                  </TableCell>
                  <TableCell>{row.implementationCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Button onClick={downLoadCSV}>CSVでダウンロード</Button>
      </Box>
    </>
  );
};
